import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import { Trans, useTranslation } from 'react-i18next';
import infoIconUrl from '@repo/assets/icons/info.svg?url';
import { ModalHeader, Modal, Button } from '@repo/ui';
import { areAllProviderNotPromoted } from '@repo/store/helpers';
import { useProviderStore } from '@repo/store/provider';
import { useIsMobile } from '@repo/mui-config/hooks';
import { useCallback, useRef } from 'react';

import { Agreements } from '@/components/agreements/agreements';
import { useMainHubStoreState } from '@/store/main-hub.store';
import { MODAL_STATE } from '@/components/modal/modal.store';
import { ModalContent } from '@/components/modal/modal-content';
import { useModalWithLocalFallback } from '@/components/modal/use-modal-with-local-fallback';
import { LiberisModalAgreements } from '@/components/welcome/loan-application-possible/liberis/agreements-liberis/liberis-modal-agreements';

import { agreements } from './agreements';

interface Props {
  isLiberisAgreementsModal?: boolean;
}

export function LiberisAgreementsForm({ isLiberisAgreementsModal }: Props) {
  const { t } = useTranslation();
  const { merchantResponseData } = useMainHubStoreState();
  const { open, isLocalModalOpen, closeLocalModal } = useModalWithLocalFallback();
  const {
    state: { isAvailableMultiprovider },
  } = useProviderStore();
  const isMobile = useIsMobile();

  if (!merchantResponseData) {
    return null;
  }

  const marketingSubmitRef = useRef<(() => void) | null>(null);
  const registerMarketingSubmit = useCallback((submit: () => void) => {
    marketingSubmitRef.current = submit;
  }, []);

  return (
    <Box marginBottom={1}>
      <Typography fontSize={18} marginBottom={2}>
        {areAllProviderNotPromoted(merchantResponseData) && isAvailableMultiprovider ? (
          <Trans
            components={[<b />]}
            i18nKey="welcomePage.loanApplicationPossible.liberis.agreementsForm.subtitleMultiprovider"
          />
        ) : (
          t('welcomePage.loanApplicationPossible.liberis.agreementsForm.subtitle')
        )}
      </Typography>

      <>
        {isLiberisAgreementsModal ? (
          <LiberisModalAgreements
            merchantResponseData={merchantResponseData}
            onRegisterMarketingSubmit={registerMarketingSubmit}
          />
        ) : (
          <Agreements
            isLiberisAgreements
            agreements={agreements}
            merchantResponseData={merchantResponseData}
            selectAllName="agreements.selectAll"
          />
        )}
      </>
      <Box
        marginTop={1}
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: 1,
        }}
      >
        <Box component="img" src={infoIconUrl} alt="info icon" width={25} height={25} />
        <Box>
          {t('welcomePage.loanApplicationPossible.liberis.agreementsForm.disclaimer')}

          <Button
            base
            onClick={() => {
              open(MODAL_STATE.LIBERIS_AGREEMENTS_ADDITIONAL_INFO);
            }}
          >
            <Typography component="span" display="inline" sx={{ ml: 0.5 }} variant="body2">
              {t('welcomePage.loanApplicationPossible.liberis.agreementsForm.disclaimerButton')}
            </Typography>
          </Button>
        </Box>
      </Box>
      <Modal
        slotProps={{
          paper: {
            sx: {
              borderRadius: '10px',
              position: 'absolute',
            },
          },
        }}
        isOpen={isLocalModalOpen}
        maxWidth="sm"
        closeModal={closeLocalModal}
      >
        <Box sx={{ py: 4, px: isMobile ? '20px' : '44px' }}>
          <ModalHeader
            centeredLogo={false}
            closeButton={{ isVisible: true, onClick: closeLocalModal }}
            sx={{ mb: '20px' }}
          />
          <ModalContent modalType={MODAL_STATE.LIBERIS_AGREEMENTS_ADDITIONAL_INFO} />
        </Box>
      </Modal>
    </Box>
  );
}
